import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuthSafe } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import type { Session } from "@supabase/supabase-js";

// Show warning 5 minutes before expiry
const WARNING_THRESHOLD_MS = 5 * 60 * 1000;
// Check interval - every 30 seconds
const CHECK_INTERVAL_MS = 30 * 1000;

interface SessionTimeoutWarningInnerProps {
  session: Session | null;
  signOut: () => Promise<void>;
}

/**
 * Inner component that contains all hooks.
 * Only rendered when auth context is available.
 */
function SessionTimeoutWarningInner({ session, signOut }: SessionTimeoutWarningInnerProps) {
  const { t } = useTranslation();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [silentRefreshAttempted, setSilentRefreshAttempted] = useState(false);
  // Guard: suppress the dialog while a silent refresh is in flight (TES-821)
  const [isSilentRefreshing, setIsSilentRefreshing] = useState(false);

  // Reset silent refresh flag when session changes (successful refresh gives new expires_at)
  useEffect(() => {
    setSilentRefreshAttempted(false);
  }, [session?.expires_at]);

  const getTimeUntilExpiry = useCallback(() => {
    if (!session?.expires_at) return null;
    const expiresAt = session.expires_at * 1000; // Convert to milliseconds
    return expiresAt - Date.now();
  }, [session]);

  const refreshSession = async () => {
    setIsRefreshing(true);
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error("Failed to refresh session:", error);
        toast.error(t("auth.sessionRefreshFailed"));
        return;
      }
      if (data.session) {
        setShowWarning(false);
        toast.success(t("auth.sessionRefreshed"));
      }
    } catch (error) {
      console.error("Error refreshing session:", error);
      toast.error(t("auth.sessionRefreshFailed"));
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = async () => {
    setShowWarning(false);
    await signOut();
  };

  useEffect(() => {
    if (!session) {
      setShowWarning(false);
      return;
    }

    const checkSession = async () => {
      const remaining = getTimeUntilExpiry();

      if (remaining === null) return;

      // Session already expired
      if (remaining <= 0) {
        setShowWarning(false);
        toast.error(t("auth.sessionExpired"), {
          description: t("auth.sessionExpiredDescription"),
        });
        signOut();
        return;
      }

      // Approaching expiry - try silent refresh first
      if (remaining <= WARNING_THRESHOLD_MS) {
        if (!silentRefreshAttempted) {
          setSilentRefreshAttempted(true);
          // Guard: don't show dialog while refresh is in flight (TES-821)
          setIsSilentRefreshing(true);
          try {
            const { error } = await supabase.auth.refreshSession();
            if (!error) {
              // Success - onAuthStateChange will update session, expires_at resets silentRefreshAttempted
              setIsSilentRefreshing(false);
              return;
            }
            console.warn("Silent session refresh failed:", error.message);
          } catch (e) {
            console.warn("Silent session refresh error:", e);
          }
          setIsSilentRefreshing(false);
          // Failed - fall through to show warning
        }
        setTimeRemaining(remaining);
        setShowWarning(true);
      } else {
        setShowWarning(false);
        setTimeRemaining(null);
      }
    };

    // Initial check
    checkSession();

    // Periodic check
    const interval = setInterval(checkSession, CHECK_INTERVAL_MS);
    // Unref the timer in Node.js to prevent blocking process exit (CI/Vitest)
    if (
      typeof (interval as ReturnType<typeof setInterval> & { unref?: () => void })?.unref ===
      "function"
    ) {
      (interval as ReturnType<typeof setInterval> & { unref?: () => void }).unref!();
    }

    return () => clearInterval(interval);
  }, [session, getTimeUntilExpiry, signOut, t, silentRefreshAttempted]);

  // Update countdown every second when warning is shown
  useEffect(() => {
    if (!showWarning) return;

    const countdownInterval = setInterval(() => {
      const remaining = getTimeUntilExpiry();
      if (remaining !== null && remaining > 0) {
        setTimeRemaining(remaining);
      }
    }, 1000);
    // Unref the timer in Node.js to prevent blocking process exit (CI/Vitest)
    if (
      typeof (countdownInterval as ReturnType<typeof setInterval> & { unref?: () => void })
        ?.unref === "function"
    ) {
      (countdownInterval as ReturnType<typeof setInterval> & { unref?: () => void }).unref!();
    }

    return () => clearInterval(countdownInterval);
  }, [showWarning, getTimeUntilExpiry]);

  const formatTimeRemaining = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Don't flash the dialog while a silent refresh is in flight (TES-821)
  if (!showWarning || isSilentRefreshing) return null;

  return (
    <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
      <AlertDialogContent className="w-[calc(100%-2rem)] sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-warning" />
            {t("auth.sessionTimeout")}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>{t("auth.sessionTimeoutDescription")}</p>
            {timeRemaining !== null && (
              <p className="text-lg font-semibold text-warning">
                {t("auth.timeRemaining")}: {formatTimeRemaining(timeRemaining)}
              </p>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={handleLogout} className="w-full sm:w-auto">
            {t("auth.logout")}
          </Button>
          <AlertDialogAction asChild>
            <Button onClick={refreshSession} disabled={isRefreshing} className="w-full sm:w-auto">
              {isRefreshing ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("common.loading")}
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t("auth.extendSession")}
                </>
              )}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

import React from "react";

/**
 * Error boundary that silently catches React dispatcher errors
 * (e.g. useState being null during OAuth callback race conditions).
 */
class SessionTimeoutErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    // Silently handle - this is expected during OAuth callback race conditions
    console.debug("SessionTimeoutWarning: suppressed error during auth flow", error.message);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

/**
 * Wrapper component that safely handles missing AuthContext.
 * Returns null if auth context is not yet available (race condition / HMR).
 */
export function SessionTimeoutWarning() {
  return (
    <SessionTimeoutErrorBoundary>
      <SessionTimeoutWarningSafe />
    </SessionTimeoutErrorBoundary>
  );
}

function SessionTimeoutWarningSafe() {
  const auth = useAuthSafe();

  // Graceful return if AuthContext not available yet
  if (!auth) return null;

  return <SessionTimeoutWarningInner session={auth.session} signOut={auth.signOut} />;
}
